import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import posthog from 'posthog-js';
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // Fetch tenant config to get auth provider
  fetch('/api/trpc/config.getTenantConfig?batch=1&input=%7B%220%22%3A%7B%22json%22%3Anull%7D%7D')
    .then(res => res.json())
    .then(data => {
      const authProvider = data?.[0]?.result?.data?.json?.authProvider;
      window.location.href = getLoginUrl(authProvider);
    })
    .catch(() => {
      // Fallback to default Manus OAuth
      window.location.href = getLoginUrl();
    });
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

const posthogOptions = {
  api_host: 'https://p.kursivia.de',
  ui_host: 'https://eu.posthog.com',
  persistence: "localStorage+cookie" as const,
  cookie_domain: ".kursivia.de",
  capture_pageview: true,
} as const;

const posthogKey = import.meta.env.VITE_POSTHOG_KEY;

// Initialize PostHog manually
if (posthogKey) {
  try {
    posthog.init(posthogKey, posthogOptions);
    // Make posthog available globally for debugging
    (window as any).posthog = posthog;
  } catch (error) {
    // do nothing
  }
} 

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
